import React, { useRef, useState, useEffect, useMemo } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { FixedSizeList } from "react-window";
import { Option, SingleValue, MultiValue, BaseProps } from "../types";
import { styles } from "./style";
import * as Icons from "react-feather";

interface Props extends BaseProps, WithStyles<typeof styles> {
	disabledOptions?: SingleValue[];
}

// Ids for helper text, so the field can point screen readers at it.
let helperTextSeq = 0;

const CustomSelectComponent: React.FC<Props> = (props) => {
	const {
		variant,
		margin,
		fullWidth,
		label,
		required,
		disabled,
		className,
		expanded,
		helperText,
		error,
		classes = {},
		multiple,
		onChange,
		value,
		options,
		valueExtractor,
		labelExtractor,
		placeholder,
		numberVisible,
		optionRenderer,
		disabledOptions,
		scrollOffset = true,
	} = props;

	const inputLabel = useRef<HTMLLabelElement>(null);
	const helperTextId = useRef(`select-helper-text-${++helperTextSeq}`).current;
	const [open, setOpen] = useState(false);
	const [labelWidth, setLabelWidth] = useState<number | undefined>(undefined);

	useEffect(() => {
		if (inputLabel.current && inputLabel.current.offsetWidth !== 0) {
			setLabelWidth(inputLabel.current.offsetWidth);
		}
	}, []);

	const values = useMemo<MultiValue>(() => {
		if (value !== undefined && value !== null) {
			if (Array.isArray(value)) {
				return value;
			} else {
				return [value];
			}
		}
		return [];
	}, [value]);

	const getOption = (val: SingleValue) =>
		options.find((option) => valueExtractor(option) === val);

	const activeOptions = useMemo<Option[]>(() => {
		return (values as SingleValue[])
			.map((val) => getOption(val))
			.filter(Boolean) as Option[];
	}, [values, options, valueExtractor]);

	const handleClick = (val: SingleValue) => {
		if (multiple) {
			const arr = [...(values as SingleValue[])];
			const i = arr.indexOf(val);
			if (i === -1) {
				arr.push(val);
			} else {
				arr.splice(i, 1);
			}
			onChange(arr);
		} else {
			setOpen(false);
			onChange(val);
		}
	};

	const selected = (option: Option) => {
		return activeOptions.includes(option);
	};

	const activeOptionIndex = useMemo(() => {
		const activeOption = activeOptions[0];
		const i = options.indexOf(activeOption);
		return Math.max(0, i);
	}, [options, activeOptions]);

	const renderPlaceholder = useMemo(() => {
		if (multiple || activeOptions.length === 0) {
			return (
				<Mui.Typography className={variant === "outlined" ? "" : classes.text}>
					{placeholder}
				</Mui.Typography>
			);
		} else {
			const option = activeOptions[0];
			return (
				<Mui.Typography className={variant === "outlined" ? "" : classes.text}>
					{labelExtractor(option)}
				</Mui.Typography>
			);
		}
	}, [
		multiple,
		activeOptions,
		variant,
		classes.text,
		placeholder,
		labelExtractor,
		valueExtractor,
	]);

	const renderFixedSizeList = () => {
		const itemHeight = 48;
		// `required` suppresses the blank/clear row — for a field that must always resolve to a
		// real option (e.g. the category destination pickers), offering an unlabeled way to select
		// "nothing" is a data-integrity trap, not a valid choice.
		const defaultOption = multiple || required ? 0 : 1;
		const itemCount = options.length + defaultOption;
		const visibleItems = Math.min(numberVisible ? numberVisible : 5, itemCount);
		const width = fullWidth ? "100%" : 338;
		const height = visibleItems * itemHeight + 1;
		const initialScrollOffset =
			(activeOptionIndex + defaultOption - (visibleItems - 1) / 2) * itemHeight;

		return (
			<FixedSizeList
				itemSize={itemHeight}
				width={width}
				height={height}
				itemCount={itemCount}
				initialScrollOffset={scrollOffset ? initialScrollOffset : 0}
			>
				{({ index, style }) => {
					if (!multiple && !required && index === 0) {
						return (
							<Mui.MenuItem
								style={style}
								onClick={() => {
									handleClick(undefined);
								}}
							>
								<em>{placeholder}</em>
							</Mui.MenuItem>
						);
					} else {
						const option = options[index - defaultOption];
						const val = valueExtractor(option);
						const isSelected = selected(option);
						const isDisabled = disabledOptions && disabledOptions.includes(val);
						return (
							<Mui.MenuItem
								style={style}
								classes={{
									selected: multiple ? classes.menuItemSelected : "",
								}}
								disabled={isDisabled}
								value={val as any}
								selected={isSelected}
								onClick={() => {
									handleClick(val);
								}}
							>
								{multiple && (
									<Mui.Checkbox checked={isSelected} className={classes.checkbox} />
								)}
								{optionRenderer(option)}
							</Mui.MenuItem>
						);
					}
				}}
			</FixedSizeList>
		);	
	};

	return (
		<Mui.FormControl
			className={className}
			variant={variant}
			margin={margin}
			fullWidth={fullWidth}
			required={required}
			disabled={disabled}
		>	
		{label && (
				<Mui.InputLabel
					ref={inputLabel as any}
					shrink={open || activeOptions.length > 0 || !!placeholder}
					classes={{ outlined: classes.outlinedLabel }}
					// `required` on FormControl above is repurposed to suppress the blank/clear
					// row (see renderFixedSizeList) — it isn't a real required-field indicator, so
					// override the inherited FormControl context here to avoid a stray "*" on the label.
					required={false}
				>
					{label}
				</Mui.InputLabel>
			)}
			{expanded ? (
				renderFixedSizeList()
			) : (
				<Mui.Select
					value=""
					renderValue={() => renderPlaceholder}
					open={open}
					onClose={() => setOpen(false)}
					onOpen={() => setOpen(true)}
					IconComponent={Icons.ChevronDown}
					displayEmpty={true}
					inputProps={{
						classes: {
							icon: classes.icon,
							selectMenu: classes.selectMenu,
						},
					}}
					label={label}
					error={error}
					SelectDisplayProps={helperText ? { "aria-describedby": helperTextId } : undefined}
				>
					{renderFixedSizeList()}
				</Mui.Select>
			)}
			{helperText && <Mui.FormHelperText id={helperTextId} error={error}>{helperText}</Mui.FormHelperText>}
		</Mui.FormControl>
	);
};

export const CustomSelect = withStyles(React.memo(CustomSelectComponent), styles);
